import React from 'react';
import { Link } from 'react-router-dom';
import { Users2, Shield, Heart, BookOpen, Globe2, Sparkles } from 'lucide-react';
import PeerHint from '@/components/PeerHint';
import EvolvingRibbon from '@/components/EvolvingRibbon';
import useSEO from '@/hooks/useSEO';

export default function About() {
    useSEO({
        title: 'About Linguistic Quest — publisher, mission & editorial policy',
        description: 'Linguistic Quest is a family-driven language-learning platform published by Southern African Traders Ltd. Learn about our mission, editorial review process, child-safety policy, and how we build puzzle-based lessons in Shona, Ndebele, Yoruba, Swahili, French and Portuguese.',
        path: '/about',
        image: '/brand/about_family.jpg',
        jsonLdId: 'about-page',
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Linguistic Quest',
            url: 'https://www.linguisticquest.com/about',
            publisher: {
                '@type': 'Organization',
                name: 'Southern African Traders Ltd',
                alternateName: 'Linguistic Quest',
                url: 'https://www.linguisticquest.com/',
                email: 'hello@linguisticquest.com',
            },
        },
    });

    return (
        <main className="max-w-4xl mx-auto px-6 py-16" data-testid="page-about">
            <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500">About</p>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">Heritage, wrapped in puzzles.</h1>
            <p className="mt-4 text-lg text-lq-ink-700 leading-relaxed max-w-2xl">
                Linguistic Quest is a family-driven language-learning platform from <strong>Southern African Traders Ltd</strong>, a UK-based publisher
                building digital products that help diaspora families, schools and curious beginners keep heritage and world languages alive.
                We believe a mother tongue should be a gift, not a chore, and that children learn languages fastest when they play them with someone they love.
            </p>

            {/* ============== GOGO & SEKURU — origin story, image, and quote ============== */}
            <section className="mt-12 bg-white border border-lq-ink-100 rounded-3xl p-6 md:p-8" data-testid="about-gogo-sekuru">
                <div className="grid md:grid-cols-[1fr,1.2fr] gap-8 items-center">
                    <figure className="rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-lq-purple-300/30 mx-auto max-w-md">
                        <img
                            src="/brand/gogo_sekuru.png"
                            alt="Gogo and Sekuru, the grandparents whose love for heritage inspired Linguistic Quest"
                            className="w-full h-auto block"
                            data-testid="about-gogo-sekuru-image"
                            loading="lazy"
                            decoding="async"
                            width="505"
                            height="508"
                        />
                        <figcaption className="bg-lq-paper border-t border-lq-ink-100 px-4 py-2 text-xs text-lq-ink-500 italic text-center">
                            Gogo &amp; Sekuru &mdash; the grandparents at the heart of our story.
                        </figcaption>
                    </figure>
                    <div>
                        <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">The story</p>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lq-ink-900">Inspired by Gogo and Sekuru.</h2>
                        <p className="mt-4 text-lq-ink-700 leading-relaxed">
                            <strong>Gogo</strong> is the Shona word for grandmother. <strong>Sekuru</strong> is grandfather. Linguistic Quest began with two of them &mdash;
                            patient, mischievous, generous &mdash; teaching their grandchildren their first words of Shona around a kitchen table in Harare and another in
                            South London. We watched a thousand-word language pass between them in the gaps of an afternoon, and realised that no app on the market
                            was built for that quiet, intergenerational way of teaching.
                        </p>
                        <p className="mt-4 text-lq-ink-700 leading-relaxed">
                            So we built one. Linguistic Quest is family-led on purpose. The grown-up holds the account; the child plays in their own profile; the
                            grandparent is invited to the screen, not replaced by it. Heritage languages survive when they&rsquo;re shared, not drilled &mdash; and our
                            puzzles are short enough to play between the dinner and the dishes.
                        </p>
                    </div>
                </div>
                <blockquote className="mt-8 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl px-6 py-5 font-display text-xl text-lq-ink-900 leading-snug">
                    <em>&ldquo;A language survives the day a grandparent and a grandchild laugh in it together.&rdquo;</em>
                    <footer className="mt-2 text-xs uppercase tracking-wider text-lq-ink-500 not-italic font-sans">Linguistic Quest founding note</footer>
                </blockquote>
                <p className="mt-6 text-sm text-lq-ink-700 leading-relaxed">
                    Africa &mdash; and Zimbabwe in particular &mdash; remains the heart of everything we publish. Six of our nine launch and upcoming languages are spoken
                    across the continent. As we extend the catalogue into France, Portugal and Spain, that African heritage is not diluted; it is the soil out of which
                    every new language grows.
                </p>
            </section>

            <figure className="mt-10 rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-lq-purple-300/30">
                <img src="/brand/about_family.jpg" alt="A mother, father and three children solving a crossword together at the kitchen table" className="w-full h-auto block" data-testid="about-family-image" loading="lazy" decoding="async" />
                <figcaption className="bg-lq-paper border-t border-lq-ink-100 px-5 py-3 text-xs text-lq-ink-500 italic">
                    A typical Linguistic Quest evening &mdash; parents, kids, a single shared crossword.
                </figcaption>
            </figure>

            <section className="mt-12 max-w-3xl" data-testid="about-story">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Why we built Linguistic Quest</h2>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Linguistic Quest was founded in 2024 by a small team of parents, educators and software engineers split between
                    the United Kingdom and Zimbabwe. We had all watched the same thing happen in our own homes and in friends&rsquo; homes:
                    a grandparent who speaks beautiful Shona, Yoruba or Portuguese sits next to a grandchild who understands only the
                    greetings. A language that survived colonisation, migration and three generations of upheaval risks quietly fading
                    in a single childhood of English-only screens.
                </p>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Conventional language apps did not solve this. They were built for adult commuters learning a second language
                    for work or holidays &mdash; long solo streaks, translation drills, and a heavy grammar focus. None of it matched
                    how heritage languages are actually transmitted in families: in short, joyful, shared moments over meals,
                    school runs and car journeys. So we built something different: a puzzle platform that brings parents, children
                    and grandparents around the same screen for five to fifteen minutes at a time.
                </p>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    The first language we launched with was Shona &mdash; because it is the language of our own families, and because
                    when we looked, almost no one else was publishing high-quality Shona learning products for children. We added
                    Ndebele, Yoruba, Swahili, French and Portuguese next, each one reviewed by native-speaker contributors from
                    Zimbabwe, Nigeria, Tanzania, Senegal and Mozambique. Spanish, Zulu, Xhosa, Amharic, Hausa, Igbo, Chichewa and
                    Afrikaans are in active preparation, with Spanish next on the calendar.
                </p>
            </section>

            <section className="mt-12 grid md:grid-cols-2 gap-5">
                <Card icon={Heart} title="Why we built this">
                    Diaspora families watch their mother tongues fade across generations. Apps teach languages as a solo grind.
                    We wanted an app that brought <em>family</em> back to the centre — grandparent and grandchild on the couch,
                    parent and child on the school run, a teacher and a class in Cardiff, Lisbon, Lagos, or Harare.
                </Card>
                <Card icon={BookOpen} title="How children learn here">
                    Six puzzle types (Word Search, Balloon Pop, Crossword, Word Scramble, Missing Vowel, Cryptogram) rotate across
                    themed vocabulary. Every foreign word reveals its translation and plays a native-speaker-style voice. Children
                    earn XP, build a personal vocabulary list, and revisit words they missed.
                </Card>
                <Card icon={Globe2} title="Languages at launch">
                    Shona, Ndebele, Yoruba, Swahili, French, Portuguese — with Spanish, Zulu, Xhosa, Amharic, Hausa, Igbo,
                    Chichewa and Afrikaans queued. Dual-flag markings for French (FR + SN) and Portuguese (PT + MZ) honour
                    where the language is spoken, not just where it started.
                </Card>
                <Card icon={Shield} title="Built for children">
                    No behavioural advertising to kids, no DM or chat between users, no dark patterns. Parents consent to each
                    child profile. Paid plans are 100% ad-free. On the free tier, short, family-friendly ads unlock three more
                    turns on a puzzle. Data stays in the UK/EU region.
                </Card>
                <Card icon={Users2} title="Heritage is shared">
                    AI voice is a helper, not a teacher. Sometimes it mis-stresses a syllable. That's where family, friends,
                    classmates and teachers step in — correcting, joking, bonding. The app is designed for collaboration,
                    not isolation.
                </Card>
                <Card icon={Sparkles} title="Always evolving">
                    The content library, puzzle types, and languages are not set in stone. New themes roll out regularly, new
                    puzzles go live without an app update, and new languages launch as our native-speaker reviewers approve them.
                </Card>
            </section>

            <section className="mt-14 max-w-3xl" data-testid="about-editorial">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Our editorial process</h2>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Every word, phrase and cultural note on Linguistic Quest is drafted by our in-house editorial team in London
                    and reviewed by at least one native-speaker contributor before it reaches a child. Our review panel is
                    distributed: Shona and Ndebele contributors in Harare and Bulawayo, Yoruba contributors in Lagos and London,
                    Swahili reviewers in Nairobi and Dar es Salaam, French reviewers in Paris and Dakar, and Portuguese reviewers
                    in Lisbon and Maputo.
                </p>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Pronunciation is produced with state-of-the-art AI voice synthesis (ElevenLabs and, where the quality is higher,
                    Microsoft Azure or Google Cloud voices for that language) and flagged for correction whenever a reviewer notices
                    a tonal or stress error. We treat the AI voice as a helpful first pass, not a final authority — and we encourage
                    families and teachers to correct it, out loud, as they play.
                </p>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Corrections are welcomed at <a href="mailto:hello@linguisticquest.com" className="text-lq-purple-700 hover:underline">hello@linguisticquest.com</a>.
                    We aim to respond to every reasonable correction within 10 working days.
                </p>
            </section>

            <section className="mt-14 max-w-3xl" data-testid="about-safety">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Child safety &amp; data posture</h2>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Linguistic Quest is designed from the ground up for use by children aged 5 and above, under the supervision of a
                    parent, carer or teacher. We do not operate chat, direct messaging, comments, photo sharing or any other
                    user-to-user feature. Children play inside a profile created and administered by their parent — never a
                    standalone account with a login of their own.
                </p>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Advertising on the free tier is limited to short, family-friendly, non-behavioural placements sold through
                    Google's H5 Games Ads framework. We do not allow advertising categories that are unsuitable for children —
                    gambling, alcohol, dating, political advocacy or adult content are excluded at source. Paid plans carry
                    no advertising at all.
                </p>
                <p className="mt-4 text-lq-ink-700 leading-relaxed">
                    Full details of how we handle data — what we collect, where it is stored, how long we keep it, and how you
                    can request deletion — are set out in our <Link to="/legal/privacy" className="text-lq-purple-700 hover:underline">Privacy Policy</Link>,
                    <Link to="/legal/cookies" className="text-lq-purple-700 hover:underline"> Cookie Policy</Link> and
                    <Link to="/legal/advertising" className="text-lq-purple-700 hover:underline"> Advertising Policy</Link>.
                </p>
            </section>

            <section className="mt-14 max-w-3xl" data-testid="about-publisher">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Publisher information</h2>
                <div className="mt-4 bg-white border border-lq-ink-100 rounded-2xl p-6 text-lq-ink-700 leading-relaxed">
                    <dl className="grid sm:grid-cols-3 gap-y-3">
                        <dt className="text-sm uppercase tracking-wide text-lq-ink-500">Publisher</dt>
                        <dd className="sm:col-span-2"><strong>Southern African Traders Ltd</strong> (trading as Linguistic Quest)</dd>

                        <dt className="text-sm uppercase tracking-wide text-lq-ink-500">Registered in</dt>
                        <dd className="sm:col-span-2">England &amp; Wales</dd>

                        <dt className="text-sm uppercase tracking-wide text-lq-ink-500">General enquiries</dt>
                        <dd className="sm:col-span-2"><a href="mailto:hello@linguisticquest.com" className="text-lq-purple-700 hover:underline">hello@linguisticquest.com</a></dd>

                        <dt className="text-sm uppercase tracking-wide text-lq-ink-500">Advertising</dt>
                        <dd className="sm:col-span-2"><a href="mailto:info@linguisticquest.com" className="text-lq-purple-700 hover:underline">info@linguisticquest.com</a></dd>

                        <dt className="text-sm uppercase tracking-wide text-lq-ink-500">Schools &amp; licensing</dt>
                        <dd className="sm:col-span-2"><Link to="/schools" className="text-lq-purple-700 hover:underline">linguisticquest.com/schools</Link></dd>

                        <dt className="text-sm uppercase tracking-wide text-lq-ink-500">Founded</dt>
                        <dd className="sm:col-span-2">2024 · first public release 2025</dd>
                    </dl>
                </div>
            </section>

            <section className="mt-12 bg-lq-purple-50 border border-lq-purple-100 rounded-2xl p-6" data-testid="about-peer-block">
                <PeerHint />
                <div className="mt-4"><EvolvingRibbon /></div>
            </section>

            <section className="mt-12">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Our promises</h2>
                <ul className="mt-4 space-y-2 text-lq-ink-700">
                    <li>• A language will never feel like homework.</li>
                    <li>• Paid plans are ad-free. Free plan plays three short ads to unlock more play.</li>
                    <li>• Advertisers must be family-safe: education, books, museums, music, apparel — never gambling, alcohol, or adult themes.</li>
                    <li>• No data is sold. Ever.</li>
                    <li>• Gift cards are permanent — they do not expire.</li>
                    <li>• New content ships continuously.</li>
                </ul>
            </section>

            <section className="mt-12">
                <h2 className="font-display text-2xl font-semibold text-lq-ink-900">Get in touch</h2>
                <p className="mt-2 text-lq-ink-700">
                    Questions, corrections, content suggestions: <a href="mailto:hello@linguisticquest.com" className="text-lq-purple-700 hover:underline">hello@linguisticquest.com</a>.{' '}
                    Advertising enquiries: <a href="mailto:info@linguisticquest.com" className="text-lq-purple-700 hover:underline">info@linguisticquest.com</a>.{' '}
                    Schools: <Link to="/schools" className="text-lq-purple-700 hover:underline">get a quote</Link>.
                </p>
            </section>
        </main>
    );
}

function Card({ icon: Icon, title, children }) {
    return (
        <div className="bg-white border border-lq-ink-100 rounded-2xl p-5">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lq-purple-100 text-lq-purple-700 mb-3">
                <Icon size={18} />
            </div>
            <h3 className="font-display text-lg font-semibold text-lq-ink-900">{title}</h3>
            <p className="mt-1 text-sm text-lq-ink-700 leading-relaxed">{children}</p>
        </div>
    );
}
